import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { parseUtc } from '../lib/calendarDate';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import ReviewForm from '../components/ReviewForm';
import StarRating from '../components/StarRating';
import ReferralSection from '../components/ReferralSection';
import AnalyticsCharts from '../components/AnalyticsCharts';
import Breadcrumbs from '../components/Breadcrumbs';
import { 
  Calendar, Clock, Plus, X, Check, Download, Video, 
  DollarSign, CreditCard, Users, Settings, Trash2, 
  ChevronRight, AlertCircle, CheckCircle, Loader2,
  TrendingUp, Eye, UserPlus, ShoppingBag, BarChart3, Star, MessageSquare, Gift
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Simple stat card component
const StatCard = ({ title, value, icon: Icon, trend, color = "purple" }) => {
  const colors = {
    purple: "from-purple-500 to-pink-500",
    blue: "from-blue-500 to-cyan-500",
    green: "from-green-500 to-emerald-500",
    orange: "from-orange-500 to-amber-500"
  };
  
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${colors[color]} rounded-xl flex items-center justify-center`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        {trend && (
          <span className={`text-sm font-medium flex items-center ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            <TrendingUp className={`h-4 w-4 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{title}</p>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'analytics');
  const [bookings, setBookings] = useState({ as_client: [], as_expert: [] });
  const [sessionTypes, setSessionTypes] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [analytics, setAnalytics] = useState({
    followers: 0,
    following: 0,
    totalRevenue: 0,
    productsSold: 0,
    sessionsCompleted: 0,
    tipsReceived: 0
  });
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(null); // { type, id, name }
  
  // New session type form
  const [showAddSession, setShowAddSession] = useState(false);
  const [newSession, setNewSession] = useState({
    title: '',
    description: '',
    duration: 60,
    price: 0,
    is_free: true
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, sessionTypesRes, availabilityRes, transactionsRes, profileRes] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }),
        axios.get(`${API}/users/${user?.user_id}/session-types`, { withCredentials: true }),
        axios.get(`${API}/users/${user?.user_id}/availability`, { withCredentials: true }),
        axios.get(`${API}/transactions`, { withCredentials: true }),
        axios.get(`${API}/users/${user?.user_id}`, { withCredentials: true })
      ]);
      
      setBookings(bookingsRes.data);
      setSessionTypes(sessionTypesRes.data.session_types || []);
      setAvailability(availabilityRes.data.availability || []);
      setTransactions(transactionsRes.data.transactions || []);
      
      // Calculate analytics
      const txns = transactionsRes.data.transactions || [];
      const incomingTxns = txns.filter(t => t.is_incoming && t.payment_status === 'completed');
      const totalRevenue = incomingTxns.reduce((sum, t) => sum + (t.amount || 0), 0);
      const productsSold = incomingTxns.filter(t => t.type === 'product_purchase').length;
      const tipsReceived = incomingTxns.filter(t => t.type === 'tip').length;
      const sessionsCompleted = (bookingsRes.data.as_expert || []).filter(b => b.status === 'confirmed').length;
      
      setAnalytics({
        followers: profileRes.data.follower_count || 0,
        following: profileRes.data.following_count || 0,
        totalRevenue,
        productsSold,
        sessionsCompleted,
        tipsReceived
      });
      
      // Load reviews data
      try {
        const [pendingRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/reviews/pending`, { withCredentials: true }),
          axios.get(`${API}/users/${user?.user_id}/reviews`, { withCredentials: true })
        ]);
        setPendingReviews(pendingRes.data.pending_reviews || []);
        setMyReviews(reviewsRes.data.reviews || []);
        setReviewStats({
          average: reviewsRes.data.average_rating,
          count: reviewsRes.data.rating_count,
          distribution: reviewsRes.data.distribution
        });
      } catch (err) {
        console.error('Error loading reviews:', err);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSessionType = async (e) => {
    e.preventDefault();
    try {
      // V1 soft launch: paid sessions are disabled — force is_free=true client-side.
      // Backend also enforces this defensively via FORCE_FREE_SESSIONS env.
      await axios.post(`${API}/session-types`, {
        ...newSession,
        price: 0,
        is_free: true
      }, { withCredentials: true });

      setNewSession({ title: '', description: '', duration: 60, price: 0, is_free: true });
      setShowAddSession(false);
      loadData();
    } catch (error) {
      console.error('Error creating session type:', error);
    }
  };

  const handleDeleteSessionType = async (sessionTypeId) => {
    if (!window.confirm('Supprimer ce type de session ?')) return;
    
    try {
      await axios.delete(`${API}/session-types/${sessionTypeId}`, { withCredentials: true });
      loadData();
    } catch (error) {
      console.error('Error deleting session type:', error);
    }
  };

  const handleUpdateAvailability = async (dayIndex, slots) => {
    const updatedAvailability = [...availability];
    const existingIndex = updatedAvailability.findIndex(a => a.day_of_week === dayIndex);
    
    if (existingIndex >= 0) {
      updatedAvailability[existingIndex] = { day_of_week: dayIndex, slots, is_available: slots.length > 0 };
    } else {
      updatedAvailability.push({ day_of_week: dayIndex, slots, is_available: slots.length > 0 });
    }
    
    try {
      await axios.put(`${API}/availability`, updatedAvailability.map(a => ({
        day_of_week: a.day_of_week,
        slots: a.slots.map(s => ({ start_time: s.start_time, end_time: s.end_time })),
        is_available: a.is_available
      })), { withCredentials: true });
      
      setAvailability(updatedAvailability);
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, { withCredentials: true });
      loadData();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  const downloadIcal = (bookingId) => {
    window.open(`${API}/bookings/${bookingId}/ical`, '_blank');
  };

  const formatDate = (dateStr) => {
    // Brief Jul 27 2026 §4 — dates backend = UTC naïf : parseUtc.
    const date = parseUtc(dateStr) || new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Breadcrumbs */}
      <Breadcrumbs />
      
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-500">Gérez vos sessions, disponibilités et paiements</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {[
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'bookings', label: 'Réservations', icon: Calendar },
            { id: 'sessions', label: 'Types de sessions', icon: Video },
            { id: 'availability', label: 'Disponibilités', icon: Clock },
            { id: 'payments', label: 'Paiements', icon: DollarSign },
            { id: 'reviews', label: 'Avis', icon: Star },
            // Referral tab masked for V1 — endpoints live in server_legacy.py (not imported)
            // To restore, uncomment and ensure /api/referrals/* are wired in routes/.
            // See /app/docs/observation_referral_program.md for V2 plan.
            // { id: 'referral', label: 'Parrainage', icon: Gift },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50/50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-purple-500" />
                Vue d'ensemble
              </h3>
              
              {/* Analytics Charts Component */}
              <AnalyticsCharts />

              {/* Quick Actions */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Actions rapides</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col"
                    onClick={() => setActiveTab('sessions')}
                  >
                    <Plus className="h-5 w-5 mb-2" />
                    <span className="text-xs">Nouvelle session</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col"
                    onClick={() => navigate(`/app/shop/${user?.user_id}`)}
                  >
                    <ShoppingBag className="h-5 w-5 mb-2" />
                    <span className="text-xs">Ma boutique</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col"
                    onClick={() => navigate('/app/assistant')}
                  >
                    <TrendingUp className="h-5 w-5 mb-2" />
                    <span className="text-xs">Assistant IA</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex-col"
                    onClick={() => navigate(`/app/profile/${user?.user_id}?view=visitor`)}
                    title="Voir mon profil comme un visiteur"
                  >
                    <Eye className="h-5 w-5 mb-2" />
                    <span className="text-xs">Voir comme visiteur</span>
                  </Button>
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Activité récente</h4>
                {transactions.length === 0 && bookings.as_expert?.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">Aucune activité récente</p>
                ) : (
                  <div className="space-y-3">
                    {[...transactions.slice(0, 3), ...(bookings.as_expert || []).slice(0, 3)]
                      .slice(0, 5)
                      .map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              item.type === 'tip' ? 'bg-orange-100' :
                              item.type === 'product_purchase' ? 'bg-green-100' :
                              'bg-blue-100'
                            }`}>
                              {item.type === 'tip' ? <CreditCard className="h-5 w-5 text-orange-600" /> :
                               item.type === 'product_purchase' ? <ShoppingBag className="h-5 w-5 text-green-600" /> :
                               <Calendar className="h-5 w-5 text-blue-600" />}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {item.type === 'tip' ? 'Tip reçu' :
                                 item.type === 'product_purchase' ? 'Vente produit' :
                                 item.session_title || 'Réservation'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.created_at ? parseUtc(item.created_at).toLocaleDateString('fr-FR') : 
                                 item.date || ''}
                              </p>
                            </div>
                          </div>
                          {item.amount && (
                            <span className="font-semibold text-green-600">
                              +{item.amount} {item.currency || 'CHF'}
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Calendar tab MOVED to /app/sessions (sprint 1.0b-γ) — Dashboard
              kept only for analytics/bookings/types/availability/payments/reviews */}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="space-y-6">
              {/* As Expert */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-purple-500" />
                  Demandes reçues ({bookings.as_expert?.length || 0})
                </h3>
                
                {bookings.as_expert?.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">Aucune demande de réservation</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.as_expert.map((booking) => (
                      <div key={booking.booking_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12" onClick={() => navigate(`/app/profile/${booking.client?.user_id}`)}>
                            <AvatarImage src={booking.client?.avatar} />
                            <AvatarFallback className="bg-purple-100 text-purple-600">
                              {booking.client?.display_name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-gray-900">{booking.client?.display_name}</p>
                            <p className="text-sm text-gray-500">
                              {booking.session_type?.title || booking.session_title || 'Session'} • {formatDate(booking.date)} à {booking.start_time}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {booking.status === 'pending' ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-500 border-red-200"
                                onClick={() => handleUpdateBookingStatus(booking.booking_id, 'cancelled')}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-500 text-white"
                                onClick={() => handleUpdateBookingStatus(booking.booking_id, 'confirmed')}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Confirmer
                              </Button>
                            </>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              booking.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                              booking.status === 'pending_payment' ? 'bg-blue-100 text-blue-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {booking.status === 'confirmed' ? 'Confirmé' :
                               booking.status === 'completed' ? 'Terminée' :
                               booking.status === 'pending_payment' ? 'Paiement en attente' : 'Annulé'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <hr className="border-gray-200" />

              {/* As Client */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-blue-500" />
                  Mes réservations ({bookings.as_client?.length || 0})
                </h3>
                
                {bookings.as_client?.length === 0 ? (
                  <p className="text-gray-400 text-center py-8">Aucune réservation</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.as_client.map((booking) => (
                      <div key={booking.booking_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12" onClick={() => navigate(`/app/profile/${booking.expert?.user_id}`)}>
                            <AvatarImage src={booking.expert?.avatar} />
                            <AvatarFallback className="bg-blue-100 text-blue-600">
                              {booking.expert?.display_name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-gray-900">{booking.expert?.display_name}</p>
                            <p className="text-sm text-gray-500">
                              {booking.session_type?.title || booking.session_title || 'Session'} • {formatDate(booking.date)} à {booking.start_time}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            booking.status === 'pending_payment' ? 'bg-blue-100 text-blue-700' :
                            booking.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {booking.status === 'confirmed' ? 'Confirmé' : 
                             booking.status === 'pending' ? 'En attente' :
                             booking.status === 'pending_payment' ? 'Paiement en attente' :
                             booking.status === 'completed' ? 'Terminée' : 'Annulé'}
                          </span>
                          {booking.status === 'confirmed' && booking.visio_link && (
                            <a
                              href={booking.visio_link}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`dashboard-join-${booking.booking_id}`}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm rounded-lg font-medium"
                              title="Rejoindre la session visio"
                            >
                              <Video className="h-4 w-4" /> Rejoindre
                            </a>
                          )}
                          {booking.status === 'confirmed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadIcal(booking.booking_id)}
                              data-testid={`dashboard-ics-${booking.booking_id}`}
                              title="Ajouter au calendrier"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Session Types Tab */}
          {activeTab === 'sessions' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Types de sessions proposées</h3>
                <Button
                  onClick={() => setShowAddSession(true)}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle session
                </Button>
              </div>

              {sessionTypes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Video className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Vous n'avez pas encore créé de type de session</p>
                  <p className="text-sm mt-1">Créez votre première offre pour commencer à recevoir des réservations</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {sessionTypes.map((st) => (
                    <div key={st.session_type_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <h4 className="font-semibold text-gray-900">{st.title}</h4>
                        {st.description && <p className="text-sm text-gray-500">{st.description}</p>}
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{st.duration} min</span>
                          <span className={st.is_free ? 'text-green-600' : 'text-purple-600'}>
                            {st.is_free ? 'Gratuit' : `${st.price} ${st.currency}`}
                          </span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteSessionType(st.session_type_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Session Modal */}
              {showAddSession && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddSession(false)} />
                  <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
                    <h3 className="text-xl font-semibold mb-4">Nouvelle session</h3>
                    <form onSubmit={handleCreateSessionType} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                        <Input
                          value={newSession.title}
                          onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                          placeholder="Ex: Coaching carrière"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={newSession.description}
                          onChange={(e) => setNewSession({ ...newSession, description: e.target.value })}
                          placeholder="Décrivez ce que vous proposez..."
                          className="w-full p-3 border border-gray-200 rounded-xl"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Durée (min)</label>
                          <Input
                            type="number"
                            value={newSession.duration}
                            onChange={(e) => setNewSession({ ...newSession, duration: parseInt(e.target.value) })}
                            min={15}
                            max={180}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Prix (CHF)
                            <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-amber-100 text-amber-700 align-middle" data-testid="session-price-soon-badge">
                              Bientôt activé
                            </span>
                          </label>
                          <Input
                            type="number"
                            value={0}
                            disabled
                            data-testid="new-session-price-input"
                            min={0}
                            step={0.5}
                            title="Les paiements seront activés prochainement. Toutes les sessions sont gratuites pendant la Beta."
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 pt-4">
                        <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddSession(false)}>
                          Annuler
                        </Button>
                        <Button type="submit" className="flex-1 bg-purple-500 text-white">
                          Créer
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Availability Tab */}
          {activeTab === 'availability' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Vos disponibilités hebdomadaires</h3>
              <p className="text-gray-500 mb-6">Définissez les créneaux où vous êtes disponible pour des sessions</p>
              
              <div className="space-y-4">
                {DAYS.map((day, idx) => {
                  const dayAvail = availability.find(a => a.day_of_week === idx);
                  const slots = dayAvail?.slots || [];
                  
                  return (
                    <div key={day} className="p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-gray-900">{day}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const newSlots = [...slots, { start_time: '09:00', end_time: '10:00' }];
                            handleUpdateAvailability(idx, newSlots);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter
                        </Button>
                      </div>
                      
                      {slots.length === 0 ? (
                        <p className="text-sm text-gray-400">Non disponible</p>
                      ) : (
                        <div className="space-y-2">
                          {slots.map((slot, slotIdx) => (
                            <div key={slotIdx} className="flex items-center gap-2">
                              <Input
                                type="time"
                                value={slot.start_time}
                                onChange={(e) => {
                                  const newSlots = [...slots];
                                  newSlots[slotIdx] = { ...slot, start_time: e.target.value };
                                  handleUpdateAvailability(idx, newSlots);
                                }}
                                className="w-28"
                              />
                              <span className="text-gray-400">-</span>
                              <Input
                                type="time"
                                value={slot.end_time}
                                onChange={(e) => {
                                  const newSlots = [...slots];
                                  newSlots[slotIdx] = { ...slot, end_time: e.target.value };
                                  handleUpdateAvailability(idx, newSlots);
                                }}
                                className="w-28"
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500"
                                onClick={() => {
                                  const newSlots = slots.filter((_, i) => i !== slotIdx);
                                  handleUpdateAvailability(idx, newSlots);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Historique des transactions</h3>
              
              {transactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune transaction</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((txn) => (
                    <div key={txn.transaction_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          txn.is_incoming ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {txn.is_incoming ? (
                            <DollarSign className="h-5 w-5 text-green-600" />
                          ) : (
                            <CreditCard className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {txn.type === 'tip' ? 'Pourboire' : 'Achat produit'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {parseUtc(txn.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${txn.is_incoming ? 'text-green-600' : 'text-gray-900'}`}>
                          {txn.is_incoming ? '+' : ''}{txn.amount} {txn.currency}
                        </p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          txn.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                          txn.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {txn.payment_status === 'completed' ? 'Complété' : 
                           txn.payment_status === 'pending' ? 'En attente' : txn.payment_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              {/* Review Stats */}
              {reviewStats && reviewStats.count > 0 && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Note moyenne</p>
                      <div className="flex items-center gap-3">
                        <span className="text-4xl font-bold text-gray-900">
                          {reviewStats.average?.toFixed(1) || 0}
                        </span>
                        <div>
                          <StarRating rating={reviewStats.average || 0} size="lg" />
                          <p className="text-sm text-gray-500 mt-1">{reviewStats.count} avis</p>
                        </div>
                      </div>
                    </div>
                    <div className="hidden sm:block">
                      {/* Rating Distribution */}
                      <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map((star) => {
                          const count = reviewStats.distribution?.[star] || 0;
                          const percent = reviewStats.count > 0 ? (count / reviewStats.count) * 100 : 0;
                          return (
                            <div key={star} className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 w-3">{star}</span>
                              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-yellow-400 rounded-full"
                                  style={{ width: `${percent}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-500 w-4">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pending Reviews to Give */}
              {pendingReviews.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <MessageSquare className="h-5 w-5 mr-2 text-purple-500" />
                    Avis à laisser ({pendingReviews.length})
                  </h3>
                  <div className="grid gap-4">
                    {pendingReviews.map((item, index) => (
                      <div key={index} className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-12 w-12">
                              <AvatarImage src={item.seller?.avatar || item.expert?.avatar} />
                              <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                                {(item.seller?.display_name || item.expert?.display_name)?.substring(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.seller?.display_name || item.expert?.display_name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {item.type === 'product' ? item.product?.title : item.session_title}
                              </p>
                              <p className="text-xs text-gray-400">
                                {item.type === 'product' ? 'Produit acheté' : `Session du ${item.date}`}
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={() => setShowReviewForm({
                              type: item.type,
                              purchaseId: item.purchase_id,
                              bookingId: item.booking_id,
                              name: item.seller?.display_name || item.expert?.display_name
                            })}
                            className="bg-gradient-to-r from-purple-500 to-pink-500"
                            data-testid={`leave-review-${index}`}
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Laisser un avis
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Received Reviews */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Star className="h-5 w-5 mr-2 text-yellow-500" />
                  Avis reçus ({myReviews.length})
                </h3>
                
                {myReviews.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl">
                    <Star className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Vous n'avez pas encore reçu d'avis</p>
                    <p className="text-sm mt-2">Les avis apparaîtront ici après vos ventes ou sessions</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myReviews.map((review) => (
                      <div key={review.review_id} className="bg-white border border-gray-200 rounded-xl p-4">
                        <div className="flex items-start gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={review.reviewer?.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white text-sm">
                              {review.reviewer?.display_name?.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-gray-900">
                                {review.reviewer?.display_name}
                              </p>
                              <StarRating rating={review.rating} size="sm" />
                            </div>
                            {review.comment && (
                              <p className="text-gray-600 text-sm mt-2">{review.comment}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-2">
                              {parseUtc(review.created_at).toLocaleDateString('fr-FR', {
                                day: 'numeric', month: 'long', year: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Referral Tab */}
          {activeTab === 'referral' && (
            <ReferralSection />
          )}
        </div>
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowReviewForm(null)}
          />
          <div className="relative w-full max-w-md mx-4">
            <ReviewForm
              purchaseId={showReviewForm.purchaseId}
              bookingId={showReviewForm.bookingId}
              targetName={showReviewForm.name}
              onSuccess={() => {
                setShowReviewForm(null);
                loadData();
              }}
              onCancel={() => setShowReviewForm(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
